// Where the timeline should FRAME a forecast that has already resolved.
//
// A live forecast frames `today → horizon` — the span still to come. A RESOLVED forecast is
// the mirror image: nothing happens after it settles and everything it did is behind us, so
// the live rule put the whole forecast left of the frame and spent the screen on the empty
// run-out to a horizon the question never reached — and once the horizon had passed,
// `autoZoom`'s span went negative and it framed nothing at all. A resolved forecast frames
// its LIFESPAN instead: created → the moment it resolved.
//
// The cap is the SAME rule the resolution marker, the probability-trace truncation, and the
// end-of-forecast line already agree on: `resolved_at` when the question settled EARLIER
// than its horizon, the horizon otherwise (an on-time or LATE resolved_at sits at or past
// the horizon line, where the marker stays put). This module is the date-space statement of
// it. `App.jsx` keeps a screen-space earliness test for the marker — deliberately: that one
// compares pixel positions, which is meaningless here, because the frame decides the pixels.
import { parseResolveDateLocal, resolutionMomentLocal } from "../formatDates.js";

/**
 * The frame a resolved forecast wants, as arguments for `autoZoom`.
 *
 * Returns `null` whenever the row is not resolved or carries no usable dates — the caller
 * then frames it live, exactly as before, so nothing about an unresolved forecast changes.
 *
 * `target` is an ISO timestamp rather than a `Date`, and carries an explicit `+00:00`
 * offset rather than `Z`, because `resolutionMomentLocal` (which `autoZoom` runs on it)
 * treats a `Date` — and any bare-midnight string, `Z` included — as a date-only value and
 * rolls it to the following midnight. A horizon cap IS midnight, and is exactly midnight
 * UTC for a viewer in UTC, so `Z` would frame a day late. The offset form takes the
 * exact-instant branch at every hour.
 *
 * @param {object} args
 * @param {boolean} args.hasTerminalVerdict whether the row has a resolved_yes/no/tie verdict
 * @param {string|null|undefined} args.resolvedAtIso when it actually resolved
 * @param {string|null|undefined} args.resolutionDate the question's scheduled horizon
 * @param {string|null|undefined} [args.question] threaded for the "before <date>" convention
 * @param {string|null|undefined} args.createdAt when the forecast was generated
 * @returns {{target: string, startDate: string|null} | null}
 */
export function resolvedFrameTarget({
  hasTerminalVerdict,
  resolvedAtIso,
  resolutionDate,
  question,
  createdAt,
}) {
  if (!hasTerminalVerdict) return null;

  const horizon = usable(resolutionMomentLocal(resolutionDate, question || ""));
  const resolved = usable(resolvedAtIso ? parseResolveDateLocal(resolvedAtIso) : null);
  // Early → the actual moment; on-time or late → the horizon, where the marker stays.
  const cap =
    resolved && horizon
      ? resolved.getTime() < horizon.getTime()
        ? resolved
        : horizon
      : (resolved ?? horizon);
  if (!cap) return null;

  const start = usable(createdAt ? new Date(createdAt) : null);
  return {
    target: asExactInstant(cap),
    // A createdAt at or after the cap is not a lifespan, so it is dropped rather than
    // inverted; autoZoom's own span guard then handles the degenerate row.
    startDate: start && start.getTime() < cap.getTime() ? start.toISOString() : null,
  };
}

/** A Date, or null when it is missing or unparseable. */
function usable(d) {
  return d && !isNaN(d.getTime()) ? d : null;
}

/**
 * Serialize an instant so `resolutionMomentLocal` reads it back as that exact instant.
 *
 * The `+00:00` offset is the whole point: its date-only test matches a trailing `Z` at
 * midnight, so `toISOString()` alone would send a midnight cap down the date-only path and
 * roll it forward a day.
 */
function asExactInstant(d) {
  return d.toISOString().replace(/Z$/, "+00:00");
}
